import { ApiFetch } from '../../../shared/utils/fetch';

/**
 * Merchant shipping zones & rates (PE-2172/PE-2173), proxied by osprey-api
 * `src/domain/shipping-zones/*` over `x-tenant-id` to logistics-service.
 *
 * ⚠️ osprey-api's update + mode routes are `@Put`, and `ApiFetch.put` actually issues a
 * PATCH on the wire (see shared/utils/fetch.ts) — use `ApiFetch.legacy_put` for both, or
 * the request lands on a route that doesn't exist.
 *
 * Responses are bare (no `{data: ...}` envelope) — unlike branches/returns-policy, there
 * is no `unwrap()` here.
 */

export const ZoneType = {
  REGION: 'REGION',
  LOCAL_PICKUP: 'LOCAL_PICKUP',
} as const;
export type ZoneType = (typeof ZoneType)[keyof typeof ZoneType];

export const RateType = {
  FLAT: 'FLAT',
  WEIGHT_BASED: 'WEIGHT_BASED',
  FREE: 'FREE',
} as const;
export type RateType = (typeof RateType)[keyof typeof RateType];

export const ShippingMode = {
  COURIER: 'COURIER',
  ZONE_BASED: 'ZONE_BASED',
} as const;
export type ShippingMode = (typeof ShippingMode)[keyof typeof ShippingMode];

/** Ascending, non-overlapping weight band. Only the last band may omit `maxWeightGrams`. */
export interface WeightTier {
  /** Upper bound in grams (inclusive). Omitted on the last tier means "and above". */
  maxWeightGrams?: number;
  /** Rate for this band, in kobo. */
  amountMinor: number;
}

export interface ShippingRate {
  /** Present on rates read back from the server; never sent on write. */
  id?: string;
  type: RateType;
  /** Kobo. Required when type is FLAT. */
  flatAmountMinor?: number;
  /** Required when type is WEIGHT_BASED. */
  weightTiers?: WeightTier[];
  currency: 'NGN';
}

export interface ShippingZone {
  id: string;
  name: string;
  /** Immutable after create. */
  type: ZoneType;
  /** ISO 3166-1 alpha-2. Required for REGION, null for LOCAL_PICKUP. */
  countryCode: string | null;
  /** Empty array = country-wide catch-all. */
  stateOrProvinceCodes: string[];
  isActive: boolean;
  /** Lower wins when more than one zone could match the same address. */
  priority: number;
  deliveryEstimateMinDays?: number;
  deliveryEstimateMaxDays?: number;
  rate: ShippingRate;
}

export interface CreateShippingZonePayload {
  name: string;
  /** Defaults to REGION server-side if omitted. */
  type?: ZoneType;
  countryCode?: string;
  stateOrProvinceCodes?: string[];
  isActive?: boolean;
  priority?: number;
  deliveryEstimateMinDays?: number;
  deliveryEstimateMaxDays?: number;
  /** Required even for LOCAL_PICKUP — send `{type: 'FREE', currency: 'NGN'}`. */
  rate: {
    type: RateType;
    flatAmountMinor?: number;
    weightTiers?: WeightTier[];
    currency?: string;
  };
}

/** `type` is immutable — never send it on update. */
export type UpdateShippingZonePayload = Partial<Omit<CreateShippingZonePayload, 'type'>>;

export interface ShippingModeState {
  scope: string;
  mode: ShippingMode;
}

export const shippingZonesApi = {
  list: (): Promise<ShippingZone[]> => ApiFetch.get('/shipping-zones'),

  create: (data: CreateShippingZonePayload): Promise<ShippingZone> =>
    ApiFetch.post('/shipping-zones', data),

  /** osprey-api's route is `@Put(':id')` — must go over the wire as PUT, not PATCH. */
  update: (id: string, data: UpdateShippingZonePayload): Promise<ShippingZone> =>
    ApiFetch.legacy_put(`/shipping-zones/${id}`, data),

  remove: (id: string): Promise<{ id: string; deleted: boolean }> =>
    ApiFetch.del(`/shipping-zones/${id}`),

  getMode: (): Promise<ShippingModeState> => ApiFetch.get('/shipping-zones/mode'),

  /** osprey-api's route is `@Put('mode')` — same PUT-not-PATCH requirement as update(). */
  setMode: (mode: ShippingMode): Promise<ShippingModeState> =>
    ApiFetch.legacy_put('/shipping-zones/mode', { mode }),
};
